
import { computed, defineComponent } from 'vue';
import { useStore } from 'vuex';
import { Actions, Getters, Mutations } from '@/store';
import ModalBody from '@/modules/modals/components/ModalBody.vue';
import ModalFooter from '@/modules/modals/components/ModalFooter.vue';
import ModalService from '@/modules/modals/services/modalService';
import Loader from '@/components/Loader.vue';
import Dropdowns from '@/modules/map-dropdowns/Dropdowns.vue';
import Notes from '@/modules/notes/components/notes.vue';
import _ from 'lodash';
import utils from '@/utils/QSUtils';
import CompsManager from '@/manager/2.6/CompsManager';
import GridColumnUtils from '@/utils/GridColumnUtils';
import { newUpsertCompDataRequestByFieldName } from '@/store/2.6/models/IUpsertCompDataRequest';
import { FieldNames } from '@/constants/FieldNames';
import LogUsageManager from '@/manager/LogUsageManager';
import FormsManager from '@/manager/2.6/FormsManager';

export default defineComponent({
    components: { ModalBody, ModalFooter, Loader, Dropdowns, Notes },
    data() {
        return {
            modal: null,
            previewPhoto: '',
            previewPhotoGuid: '',
            isLoading: false,
            dropdownsCallback: [],
            notesCallback: { notes: '', hasError: false },
            photoGallery: {},
            photoSortOrder: {},
        };
    },
    setup() {
        const store = useStore();

        const computedActiveComp = computed(() => store.getters[Getters.CompAddresses.ActiveComp]);
        const activeComp = _.cloneDeep(computedActiveComp.value);
        const compPhotoNotes = activeComp.notesData !== null && !utils.isObjectEmpty(activeComp.notesData) && activeComp.notesData.find((f: any) => f.fieldName === 'COMP_PHOTO') ? activeComp.notesData.find((f: any) => f.fieldName === 'COMP_PHOTO').value : '';

        const photos = CompsManager.getActiveCompPhotos();

        console.log(photos);

        return {
            store,
            activeComp,
            photos,
            compPhotoNotes,
        };
    },
    async mounted() {
        const modalElement = this.$refs.photoView;
        this.modal = ModalService.initialize(modalElement, this);
        ModalService.size(modalElement, 'auto');
        ModalService.open(this.modal);

        this.isLoading = true;

        this.previewPhoto = this.activeComp.sourceData.find((f: any) => f.fieldName === 'COMP_PHOTO') ? this.activeComp.sourceData.find((f: any) => f.fieldName === 'COMP_PHOTO').value : '';
        this.previewPhotoGuid = this.photos.find((f: any) => f.full === this.previewPhoto)?.guid || '';

        const fieldData = await FormsManager.getFieldDataById(644);
        this.photoSortOrder = GridColumnUtils.cleanDataPrioritiesStringDelimiter(fieldData?.flattenedDataPriorities ?? '');

        this.processPhotoData();
        await this.initialize();

        this.isLoading = false;

        LogUsageManager.launchedViewPhotos();
    },
    methods: {
        async initialize() {
            window.addEventListener('resize', this.resizeColumns);
            await utils.sleep(350);
            this.resizeColumns();
        },
        preview(photo: string, guid: string) {
            this.previewPhoto = photo;
            this.previewPhotoGuid = guid;
        },
        isActivePhoto(guid: string): boolean {
            return guid === this.previewPhotoGuid ? true : false;
        },
        processPhotoData() {
            let photos = {} as any;
            for (const item in this.photos) {
                const sourceInitial = this.photos[item].sourceInitial.toLowerCase();
                const photoFull = this.photos[item].full;
                const photoThumbnail = this.photos[item].thumbnail;
                const photoGuid = this.photos[item].guid;
                if (!utils.isKeyInObject(photos, sourceInitial)) {
                    const sourceInitialDetails = utils.initialDetails(sourceInitial);
                    photos[sourceInitial] = {
                        count: 1,
                        sourceInitial: sourceInitial.toLowerCase(),
                        label: sourceInitialDetails.title,
                        photo: [
                            {
                                full: photoFull,
                                thumbnail: photoThumbnail,
                                guid: photoGuid,
                            },
                        ],
                    };
                } else {
                    photos[sourceInitial].count = photos[sourceInitial].count + 1;
                    photos[sourceInitial].photo.push({
                        full: photoFull,
                        thumbnail: photoThumbnail,
                        guid: photoGuid,
                    });
                }
            }

            this.photoGallery = this.sortPhotosByDataPriorities(photos);
        },
        sortPhotosByDataPriorities(photos: any) {
            const sortBy = this.photoSortOrder;
            const photosList = {} as any;
            for (const idx in sortBy) {
                const initial = (sortBy as any)[idx].toLowerCase();
                if (photos[initial] !== undefined) {
                    photosList[initial] = photos[initial];
                }
            }
            return photosList;
        },
        resizeColumns() {
            const elLeft = document.getElementById('mlsContainerLeft') as HTMLElement;
            const elPhotoSection = document.getElementById('photosSection') as HTMLElement;
            const elRight = document.getElementById('mlsContainerRight') as HTMLElement;
            const modalBodyPosition = utils.getElementPositionById('modalBody');
            const bannerText = utils.getElementPositionById('bannerText');

            const constants = modalBodyPosition.height - bannerText.height;

            elPhotoSection.style.height = `${constants - 180}px`;

            // elLeft.style.height = `${constants - 30}px`;
            // elRight.style.height = `${constants - 30}px`;
        },
        async save() {
            if (this.notesCallback.hasError) return;

            // Save dropdown selections
            for (const idx in this.dropdownsCallback) {
                const field = this.dropdownsCallback[idx];
                const fieldId = field['fieldId'];
                for (var key of Object.keys(field)) {
                    if (key === 'fieldId' || key === 'fieldName') {
                        continue;
                    }

                    const fieldData = await FormsManager.getFieldDataById(fieldId);
                    const comp = computed(() => this.store.getters[Getters.CompAddresses.ActiveComp]);
                    this.store.commit(Mutations.CompAddresses.UpsertCompSourceDataValueByFieldName, {
                        comp: comp.value,
                        fieldName: fieldData?.fieldName,
                        name: key,
                        value: field[key],
                    });
                }
            }

            // Preview Photo
            const request = newUpsertCompDataRequestByFieldName(this.activeComp, FieldNames.CompPhoto);
            request.value = this.previewPhoto;
            request.description = 'PhotoView.save()';
            this.store.commit(Mutations.CompAddresses.UpsertCompSourceDataByFieldName, request);

            // Update Comp Photo Notes
            this.store.dispatch(Actions.CompAddresses.UpdateInsertNotes, { fieldName: 'COMP_PHOTO', value: this.notesCallback.notes });

            LogUsageManager.savedPhotoViewChanges();

            ModalService.hide(this.modal);
        },
        NotesCallback(value: any) {
            this.notesCallback = value;
        },
        DropdownsCallback(value: any) {
            this.dropdownsCallback = value;
        },
    },
});
